import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionHead } from "./Reveal";

const STAGES = [
  {
    num: "01",
    title: "Direction & Tone",
    desc: "Reference mining, moodboards, visual language and the discipline of taste. You learn to defend a creative decision before you ever touch a camera.",
    tags: ["Moodboards", "Script Breakdowns", "Visual References"],
  },
  {
    num: "02",
    title: "Production & Camera",
    desc: "Cinema cameras, lighting design, blocking and set etiquette. Shoot real spec commercials on a working studio floor, not a classroom corner.",
    tags: ["Cinema Cameras", "Lighting", "Set Craft"],
  },
  {
    num: "03",
    title: "AI Synthesis & VFX",
    desc: "Generative pipelines as a director's tool — concept frames, previs, extensions and hybrids that blend captured footage with AI-generated worlds.",
    tags: ["ComfyUI", "Runway", "Previs"],
  },
  {
    num: "04",
    title: "Client Delivery & Polish",
    desc: "Edit, grade, mix, version and package. Present to real clients, take real notes, and ship broadcast-ready deliverables with your name on them.",
    tags: ["Color Grade", "Sound Mix", "Client Sign-Off"],
  },
];

export const Method = () => {
  const [active, setActive] = useState(0);
  const stage = STAGES[active];

  return (
    <section id="method" data-testid="parallel-method-section" className="bg-obsidian py-28 sm:py-40">
      <div className="max-w-[1600px] mx-auto px-5 sm:px-10">
        <SectionHead num="05" eyebrow="How It Works" title={<>The PARA<span className="text-acid">{"//"}</span>EL Method</>} />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
          {STAGES.map((s, i) => (
            <button
              key={s.num}
              data-testid={`method-stage-${s.num}`}
              onClick={() => setActive(i)}
              className={`text-left p-6 sm:p-8 transition-colors duration-300 ${
                active === i ? "bg-surface" : "bg-obsidian hover:bg-ink"
              }`}
            >
              <span className={`font-mono text-xs font-bold tracking-[0.25em] ${active === i ? "text-acid" : "text-dim"}`}>{s.num}</span>
              <span className={`block mt-4 font-head text-base sm:text-lg font-semibold uppercase tracking-tight leading-tight ${active === i ? "text-cream" : "text-smoke"}`}>
                {s.title}
              </span>
              <span className={`block mt-6 h-0.5 transition-all duration-500 ${active === i ? "bg-acid w-full" : "bg-line w-8"}`} />
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 border border-line bg-ink p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8"
            data-testid="method-detail"
          >
            <div className="lg:col-span-8">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-acid">Stage {stage.num}</span>
              <p className="mt-4 text-base sm:text-lg text-smoke leading-relaxed max-w-2xl">{stage.desc}</p>
            </div>
            <div className="lg:col-span-4 flex flex-wrap lg:flex-col gap-3 lg:items-end">
              {stage.tags.map((t) => (
                <span key={t} className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream border border-line px-4 py-2">
                  {t}
                </span>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <Reveal className="mt-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-dim">Four stages. One pipeline. Zero busywork.</p>
        </Reveal>
      </div>
    </section>
  );
};
